import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { styled, alpha } from '@mui/material/styles';
import Box from '@mui/material/Box';
import MuiDrawer from '@mui/material/Drawer';
import MuiAppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import List from '@mui/material/List';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Avatar from '@mui/material/Avatar';
import Badge from '@mui/material/Badge';
import Tooltip from '@mui/material/Tooltip';
import InputBase from '@mui/material/InputBase';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import ListSubheader from '@mui/material/ListSubheader';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Divider from '@mui/material/Divider';
import Paper from '@mui/material/Paper';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import MenuIcon from '@mui/icons-material/Menu';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import DashboardIcon from '@mui/icons-material/Dashboard';
import CategoryIcon from '@mui/icons-material/Category';
import AssignmentIcon from '@mui/icons-material/Assignment';
import PeopleIcon from '@mui/icons-material/People';
import InventoryIcon from '@mui/icons-material/Inventory';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import LogoutIcon from '@mui/icons-material/Logout';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ArchiveIcon from '@mui/icons-material/Archive';
import NotificationsIcon from '@mui/icons-material/Notifications';
import SearchIcon from '@mui/icons-material/Search';
import ViewListIcon from '@mui/icons-material/ViewList';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import InfoIcon from '@mui/icons-material/Info';
import CloseIcon from '@mui/icons-material/Close';
import HistoryIcon from '@mui/icons-material/History';
import KeyboardReturnIcon from '@mui/icons-material/KeyboardReturn';
import ChairIcon from '@mui/icons-material/Chair';
import PersonIcon from '@mui/icons-material/Person';
import ColorLensIcon from '@mui/icons-material/ColorLens';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import { globalSearch, getNotifications, markNotificationRead, markAllNotificationsRead, getUnreadCount } from '../api';
import { can } from '../capabilities';

const drawerWidth = 280;

// Стилизованный AppBar с современным дизайном
const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== 'sidebarWidth',
})(({ theme, sidebarWidth }) => ({
  backgroundColor: '#ffffff',
  color: theme.palette.text.primary,
  boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.05), 0 1px 2px -1px rgba(0, 0, 0, 0.05)',
  borderBottom: '1px solid rgba(0, 0, 0, 0.06)',
  zIndex: theme.zIndex.appBar,
  marginLeft: sidebarWidth,
  width: `calc(100% - ${sidebarWidth}px)`,
  transition: theme.transitions.create(['width', 'margin'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
}));

// Стилизованный Drawer с темной синей темой
const Drawer = styled(MuiDrawer)(
  ({ theme, open }) => ({
    flexShrink: 0,
    '&.MuiDrawer-docked': {
      width: open ? drawerWidth : theme.spacing(9),
      transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
      }),
    },
    '& .MuiDrawer-paper': {
      position: 'fixed',
      height: '100dvh',
      overflow: 'hidden',
      whiteSpace: 'nowrap',
      width: drawerWidth,
      background: 'linear-gradient(180deg, #0f172a 0%, #1e293b 100%)',
      color: '#e2e8f0',
      transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
      }),
      boxSizing: 'border-box',
      borderRight: 'none',
      borderRadius: 0,
      boxShadow: '4px 0 24px rgba(0, 0, 0, 0.15)',
      ...(!open && {
        overflowX: 'hidden',
        transition: theme.transitions.create('width', {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.leavingScreen,
        }),
        width: theme.spacing(9),
        [theme.breakpoints.up('sm')]: {
          width: theme.spacing(9),
        },
      }),
    },
  }),
);

// Поле поиска в AppBar
const Search = styled('div')(({ theme }) => ({
  position: 'relative',
  borderRadius: 8,
  backgroundColor: alpha(theme.palette.primary.main, 0.04),
  border: '1px solid rgba(0, 0, 0, 0.08)',
  display: 'flex',
  alignItems: 'center',
  height: 40,
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.08),
    borderColor: theme.palette.primary.main,
  },
  marginRight: theme.spacing(2),
  marginLeft: 0,
  width: '100%',
  transition: 'all 0.2s ease',
}));

const SearchIconWrapper = styled('div')(({ theme }) => ({
  padding: theme.spacing(0, 1.5),
  height: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: theme.palette.text.secondary,
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
  color: 'inherit',
  flex: 1,
  '& .MuiInputBase-input': {
    padding: theme.spacing(1, 1, 1, 0),
    fontSize: '0.875rem',
    '&::placeholder': {
      color: '#94a3b8',
      opacity: 1,
    },
  },
}));

// Стилизованная кнопка меню
const StyledListItemButton = styled(ListItemButton)(({ theme }) => ({
  borderRadius: 10,
  margin: '3px 12px',
  padding: '10px 16px',
  color: '#94a3b8',
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.08)',
    color: '#ffffff',
    transform: 'translateX(4px)',
  },
  '&.Mui-selected': {
    background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)',
    color: '#ffffff',
    boxShadow: '0 4px 12px rgba(30, 64, 175, 0.4)',
    '&:hover': {
      background: 'linear-gradient(135deg, #1e3a8a 0%, #1e40af 100%)',
      transform: 'translateX(0)',
    },
    '& .MuiListItemIcon-root': {
      color: '#ffffff',
    },
  },
}));

export default function Layout({ user, onLogout }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [open, setOpen] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchFocused, setSearchFocused] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [recentSearches, setRecentSearches] = useState(() => {
    const saved = localStorage.getItem('recentSearches');
    if (!saved) return [];
    try {
      const parsed = JSON.parse(saved);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      localStorage.removeItem('recentSearches');
      return [];
    }
  });
  const [notificationsAnchor, setNotificationsAnchor] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notificationsLoading, setNotificationsLoading] = useState(false);
  const searchRef = useRef(null);
  const searchSequenceRef = useRef(0);
  const notificationSequenceRef = useRef(0);
  const notificationControllerRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, [location.pathname]);

  useEffect(() => {
    setOpen(!isMobile);
  }, [isMobile]);

  // Быстрые ссылки для поиска
  const quickLinks = [
    { capability: 'viewDashboard', label: 'Панель управления', path: '/', icon: <DashboardIcon fontSize="small" />, keywords: ['главная', 'дашборд', 'статистика'] },
    { capability: 'viewPlan', label: 'План производства', path: '/plan', icon: <AssignmentIcon fontSize="small" />, keywords: ['план', 'диваны', 'производство'] },
    { capability: 'viewTasks', label: 'Задачи', path: '/tasks', icon: <ViewListIcon fontSize="small" />, keywords: ['задачи', 'работа', 'статус'] },
    { capability: 'viewWorkers', label: 'Сотрудники', path: '/workers', icon: <PeopleIcon fontSize="small" />, keywords: ['работники', 'персонал', 'швея', 'каркасник'] },
    { capability: 'viewInventory', label: 'Ткани в наличии', path: '/fabrics', icon: <ColorLensIcon fontSize="small" />, keywords: ['ткань', 'цвет', 'велюр', 'рогожка'] },
    { capability: 'viewInventory', label: 'Материалы', path: '/materials', icon: <InventoryIcon fontSize="small" />, keywords: ['материал', 'склад', 'остатки'] },
    { capability: 'viewDeliveries', label: 'Доставки', path: '/deliveries', icon: <LocalShippingIcon fontSize="small" />, keywords: ['доставка', 'водитель', 'адрес'] },
    { capability: 'viewCompanySalary', label: 'Зарплаты', path: '/salary', icon: <AttachMoneyIcon fontSize="small" />, keywords: ['зарплата', 'оплата', 'деньги'] },
    { capability: 'viewForecast', label: 'Прогнозы', path: '/forecast', icon: <TrendingUpIcon fontSize="small" />, keywords: ['прогноз', 'аналитика', 'автоматизация'] },
    { capability: 'manageModels', label: 'Модели диванов', path: '/models', icon: <CategoryIcon fontSize="small" />, keywords: ['модель', 'тип', 'диван'] },
    { capability: 'viewPlan', label: 'Архив', path: '/archive', icon: <ArchiveIcon fontSize="small" />, keywords: ['архив', 'история', 'старые'] },
  ].filter(link => can(user, link.capability));

  // Поиск по данным
  const performSearch = useCallback(async (query, sequence) => {
    if (!query || query.length < 2) {
      setSearchResults([]);
      setSearchLoading(false);
      return;
    }

    setSearchLoading(true);
    const results = [];
    const lowerQuery = query.toLowerCase();

    // Поиск по быстрым ссылкам (разделы)
    quickLinks.forEach(link => {
      const matchLabel = link.label.toLowerCase().includes(lowerQuery);
      const matchKeywords = link.keywords.some(k => k.includes(lowerQuery));
      if (matchLabel || matchKeywords) {
        results.push({
          type: 'page',
          title: link.label,
          subtitle: 'Раздел системы',
          icon: link.icon,
          path: link.path,
        });
      }
    });

    try {
      const response = await globalSearch(query, Math.max(1, 10 - results.length));
      if (sequence !== searchSequenceRef.current) return;
      (response.data?.items || []).forEach(item => results.push({
        ...item,
        icon: item.type === 'user'
          ? <PersonIcon fontSize="small" sx={{ color: '#475569' }} />
          : item.type === 'delivery'
            ? <LocalShippingIcon fontSize="small" sx={{ color: '#7c3aed' }} />
            : <ChairIcon fontSize="small" sx={{ color: '#1e40af' }} />,
      }));
    } catch (error) {
      if (sequence !== searchSequenceRef.current) return;
      console.error('Global search error:', error);
    }

    setSearchResults(results.slice(0, 10)); // Ограничиваем 10 результатами
    setSearchLoading(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Debounce поиска
  useEffect(() => {
    const sequence = ++searchSequenceRef.current;
    const timer = setTimeout(() => {
      if (searchQuery) {
        performSearch(searchQuery, sequence);
      } else {
        setSearchResults([]);
        setSearchLoading(false);
      }
    }, 300);
    return () => {
      clearTimeout(timer);
      if (searchSequenceRef.current === sequence) searchSequenceRef.current += 1;
    };
  }, [searchQuery, performSearch]);

  // Сохранение недавнего поиска
  const saveRecentSearch = (query) => {
    if (!query || query.length < 2) return;
    const updated = [query, ...recentSearches.filter(s => s !== query)].slice(0, 5);
    setRecentSearches(updated);
    localStorage.setItem('recentSearches', JSON.stringify(updated));
  };

  // Обработка выбора результата
  const handleResultClick = (result) => {
    saveRecentSearch(searchQuery);
    setSearchQuery('');
    setSearchFocused(false);
    navigate(result.path);
  };

  // Обработка нажатия Enter
  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter' && searchResults.length > 0) {
      handleResultClick(searchResults[0]);
    }
    if (e.key === 'Escape') {
      setSearchFocused(false);
      setSearchQuery('');
    }
  };

  // Очистка недавних поисков
  const clearRecentSearches = () => {
    setRecentSearches([]);
    localStorage.removeItem('recentSearches');
  };

  const toggleDrawer = () => setOpen((value) => !value);
  
  const handleNotificationsOpen = (event) => {
    setNotificationsAnchor(event.currentTarget);
  };
  
  const handleNotificationsClose = () => {
    setNotificationsAnchor(null);
  };

  // Загрузка уведомлений из API
  const loadNotifications = useCallback(async () => {
    const sequence = ++notificationSequenceRef.current;
    notificationControllerRef.current?.abort();
    const controller = new AbortController();
    notificationControllerRef.current = controller;
    try {
      setNotificationsLoading(true);
      const [response, countResponse] = await Promise.all([
        getNotifications({ limit: 20, unread_only: true }, { signal: controller.signal }),
        getUnreadCount(),
      ]);
      if (sequence !== notificationSequenceRef.current) return;
      if (response.data) {
        setNotifications(response.data);
      }
      if (countResponse.data) {
        setUnreadCount(countResponse.data.count || 0);
      }
    } catch (error) {
      if (error.code === 'ERR_CANCELED' || sequence !== notificationSequenceRef.current) return;
      console.error('Ошибка загрузки уведомлений:', error);
      // Если API недоступен, показываем пустой список
      setNotifications([]);
      setUnreadCount(0);
    } finally {
      if (sequence === notificationSequenceRef.current) setNotificationsLoading(false);
    }
  }, []);

  // Загрузка уведомлений при монтировании и каждые 30 секунд
  useEffect(() => {
    loadNotifications();
    const interval = setInterval(loadNotifications, 30000);
    return () => {
      clearInterval(interval);
      notificationControllerRef.current?.abort();
    };
  }, [loadNotifications]);

  // Пометить уведомление как прочитанное
  const handleMarkRead = async (id) => {
    try {
      await markNotificationRead(id);
      setNotifications(prev => prev.filter(n => n.id !== id));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Ошибка при пометке уведомления:', error);
    }
  };

  // Пометить все как прочитанные
  const handleMarkAllRead = async () => {
    try {
      await markAllNotificationsRead();
      setNotifications([]);
      setUnreadCount(0);
      handleNotificationsClose();
    } catch (error) {
      console.error('Ошибка при пометке всех уведомлений:', error);
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'warning': return <WarningIcon sx={{ color: '#d97706' }} />;
      case 'success': return <CheckCircleIcon sx={{ color: '#059669' }} />;
      case 'info': return <InfoIcon sx={{ color: '#1e40af' }} />;
      default: return <InfoIcon sx={{ color: '#6b7280' }} />;
    }
  };

  const getRoleDisplayName = (role) => {
    const roles = {
      admin: 'Администратор',
      manager: 'Менеджер',
      frame_worker: 'Каркасник',
      sewer: 'Швея',
      upholsterer: 'Обстрельщик',
      driver: 'Водитель',
    };
    return roles[role] || role;
  };

  const getInitials = (name) => {
    if (!name) return 'U';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const getRoleColor = (role) => {
    const colors = {
      admin: '#374151',
      manager: '#1e40af',
      frame_worker: '#475569',
      sewer: '#64748b',
      upholsterer: '#334155',
      driver: '#4b5563',
    };
    return colors[role] || '#6b7280';
  };

  // Главные пункты меню
  const mainMenuItems = [
    can(user, 'viewDashboard') && { text: 'Панель управления', icon: <DashboardIcon />, path: '/' },
    !can(user, 'viewDashboard') && can(user, 'viewDeliveries') && { text: 'Доставки', icon: <LocalShippingIcon />, path: '/deliveries' },
  ].filter(Boolean);

  // Производство
  const productionItems = [
    can(user, 'viewPlan') && { text: 'План производства', icon: <AssignmentIcon />, path: '/plan' },
    can(user, 'viewTasks') && { text: 'Задачи', icon: <ViewListIcon />, path: '/tasks' },
    can(user, 'manageModels') && { text: 'Модели диванов', icon: <CategoryIcon />, path: '/models' },
    can(user, 'viewPlan') && { text: 'Архив', icon: <ArchiveIcon />, path: '/archive' },
  ].filter(Boolean);

  // Ресурсы
  const resourceItems = [
    can(user, 'viewWorkers') && { text: 'Сотрудники', icon: <PeopleIcon />, path: '/workers' },
    can(user, 'viewInventory') && { text: 'Ткани в наличии', icon: <ColorLensIcon />, path: '/fabrics' },
    can(user, 'viewInventory') && { text: 'Материалы', icon: <InventoryIcon />, path: '/materials' },
    can(user, 'viewDeliveries') && user?.role !== 'driver' && { text: 'Доставки', icon: <LocalShippingIcon />, path: '/deliveries' },
  ].filter(Boolean);

  // Финансы
  const financeItems = [
    can(user, 'viewCompanySalary') && { text: 'Зарплаты', icon: <AttachMoneyIcon />, path: '/salary' },
    can(user, 'viewForecast') && { text: 'Прогнозы', icon: <TrendingUpIcon />, path: '/forecast' },
  ].filter(Boolean);

  // Рендер одного пункта меню
  const renderMenuItem = (item) => (
    <ListItem key={item.text} disablePadding sx={{ display: 'block' }}>
      <StyledListItemButton
        selected={item.path === '/' ? location.pathname === '/' : location.pathname === item.path || location.pathname.startsWith(`${item.path}/`)}
        onClick={() => { navigate(item.path); if (isMobile) setOpen(false); }}
        sx={{ justifyContent: open ? 'initial' : 'center', px: 2.5 }}
      >
        <ListItemIcon sx={{ 
          minWidth: 0, 
          mr: open ? 2 : 'auto', 
          justifyContent: 'center',
          color: (item.path === '/' ? location.pathname === '/' : location.pathname === item.path || location.pathname.startsWith(`${item.path}/`)) ? '#ffffff' : '#94a3b8'
        }}>
          {item.icon}
        </ListItemIcon>
        <ListItemText 
          primary={item.text} 
          sx={{ opacity: open ? 1 : 0 }}
          primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: 500 }}
        />
      </StyledListItemButton>
    </ListItem>
  );

  // Рендер секции меню
  const renderSection = (title, items) => (
    <React.Fragment key={title}>
      {open && (
        <ListSubheader sx={{ 
          bgcolor: 'transparent', 
          color: '#64748b', 
          fontSize: '0.7rem',
          fontWeight: 700,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          mt: 2.5,
          mb: 0.5,
          lineHeight: '28px',
          px: 3,
        }}>
          {title}
        </ListSubheader>
      )}
      {items.map(renderMenuItem)}
    </React.Fragment>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100dvh', width: '100%' }}>
      {/* AppBar */}
      <AppBar position="fixed" sidebarWidth={isMobile ? 0 : open ? drawerWidth : 72} elevation={0}>
        <Toolbar sx={{ px: { xs: 1, sm: 2, md: 3 }, minHeight: '64px !important' }}>
          <IconButton
            edge="start"
            color="inherit"
            aria-label="toggle drawer"
            onClick={toggleDrawer}
            sx={{
              marginRight: 2,
              borderRadius: 2,
              '&:hover': {
                backgroundColor: 'rgba(30, 64, 175, 0.08)',
              },
            }}
          >
            {open ? <ChevronLeftIcon /> : <MenuIcon />}
          </IconButton>

          {/* Поиск */}
          <ClickAwayListener onClickAway={() => setSearchFocused(false)}>
            <Box sx={{ position: 'relative', width: 320, minWidth: 0, flexShrink: 1, display: { xs: 'none', md: 'block' } }} ref={searchRef}>
              <Search sx={{ 
                border: searchFocused ? '2px solid #1e40af' : '1px solid rgba(0, 0, 0, 0.08)',
                boxShadow: searchFocused ? '0 0 0 3px rgba(30, 64, 175, 0.1)' : 'none',
                bgcolor: searchFocused ? '#fff' : undefined,
              }}>
                <SearchIconWrapper>
                  {searchLoading ? (
                    <CircularProgress size={16} sx={{ color: '#64748b' }} />
                  ) : (
                    <SearchIcon sx={{ fontSize: 20, color: '#64748b' }} />
                  )}
                </SearchIconWrapper>
                <StyledInputBase
                  placeholder="Поиск..."
                  role="combobox"
                  aria-label="Поиск по системе"
                  aria-expanded={searchFocused}
                  aria-controls="global-search-results"
                  aria-autocomplete="list"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onFocus={() => setSearchFocused(true)}
                  onKeyDown={handleSearchKeyDown}
                />
                {searchQuery && (
                  <IconButton 
                    aria-label="Очистить поиск"
                    size="small" 
                    onClick={() => setSearchQuery('')}
                    sx={{ p: 0.5, mr: 0.5, color: '#94a3b8' }}
                  >
                    <CloseIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                )}
              </Search>

              {/* Выпадающие результаты поиска */}
              {searchFocused && (
                <Paper
                  id="global-search-results"
                  role="listbox"
                  elevation={8}
                  sx={{
                    position: 'absolute',
                    top: 'calc(100% + 4px)',
                    left: 0,
                    width: 320,
                    borderRadius: 2,
                    overflow: 'hidden',
                    zIndex: 1300,
                    maxHeight: 350,
                    overflowY: 'auto',
                    border: '1px solid #e2e8f0',
                    boxShadow: '0 10px 40px rgba(0,0,0,0.12)',
                  }}
                >
                  {/* Недавние поиски */}
                  {!searchQuery && recentSearches.length > 0 && (
                    <Box>
                      <Box sx={{ 
                        px: 2, 
                        py: 1.5, 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'space-between',
                        borderBottom: '1px solid #f1f5f9'
                      }}>
                        <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                          Недавние поиски
                        </Typography>
                        <Chip 
                          label="Очистить" 
                          size="small" 
                          onClick={clearRecentSearches}
                          sx={{ 
                            height: 22, 
                            fontSize: '0.7rem',
                            cursor: 'pointer',
                            '&:hover': { bgcolor: '#f1f5f9' }
                          }} 
                        />
                      </Box>
                      {recentSearches.map((search, idx) => (
                        <ListItemButton 
                          role="option"
                          aria-selected="false"
                          key={idx}
                          onClick={() => setSearchQuery(search)}
                          sx={{ py: 1.25, px: 2 }}
                        >
                          <ListItemIcon sx={{ minWidth: 36 }}>
                            <HistoryIcon fontSize="small" sx={{ color: '#94a3b8' }} />
                          </ListItemIcon>
                          <ListItemText 
                            primary={search}
                            primaryTypographyProps={{ fontSize: '0.875rem' }}
                          />
                        </ListItemButton>
                      ))}
                    </Box>
                  )}

                  {/* Быстрые ссылки */}
                  {!searchQuery && recentSearches.length === 0 && (
                    <Box>
                      <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid #f1f5f9' }}>
                        <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                          Быстрый переход
                        </Typography>
                      </Box>
                      {quickLinks.slice(0, 6).map((link, idx) => (
                        <ListItemButton 
                          key={idx}
                          onClick={() => { navigate(link.path); setSearchFocused(false); }}
                          sx={{ py: 1.25, px: 2 }}
                        >
                          <ListItemIcon sx={{ minWidth: 36, color: '#1e40af' }}>
                            {link.icon}
                          </ListItemIcon>
                          <ListItemText 
                            primary={link.label}
                            primaryTypographyProps={{ fontSize: '0.875rem' }}
                          />
                          <KeyboardReturnIcon fontSize="small" sx={{ color: '#cbd5e1' }} />
                        </ListItemButton>
                      ))}
                    </Box>
                  )}

                  {/* Результаты поиска */}
                  {searchQuery && searchResults.length > 0 && (
                    <Box>
                      <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid #f1f5f9' }}>
                        <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                          Результаты ({searchResults.length})
                        </Typography>
                      </Box>
                      {searchResults.map((result, idx) => (
                        <ListItemButton 
                          key={idx}
                          onClick={() => handleResultClick(result)}
                          sx={{ 
                            py: 1.5, 
                            px: 2,
                            '&:hover': { bgcolor: '#f8fafc' }
                          }}
                        >
                          <ListItemIcon sx={{ minWidth: 40 }}>
                            <Avatar sx={{ 
                              width: 32, 
                              height: 32, 
                              bgcolor: result.type === 'sofa' ? '#eff6ff' : result.type === 'user' ? '#f1f5f9' : '#f0fdf4',
                              color: result.type === 'sofa' ? '#1e40af' : result.type === 'user' ? '#475569' : '#059669'
                            }}>
                              {result.icon}
                            </Avatar>
                          </ListItemIcon>
                          <ListItemText 
                            primary={result.title}
                            secondary={result.subtitle}
                            primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: 500 }}
                            secondaryTypographyProps={{ fontSize: '0.75rem' }}
                          />
                          <Chip 
                            label={result.type === 'sofa' ? 'Диван' : result.type === 'user' ? 'Сотрудник' : 'Раздел'}
                            size="small"
                            sx={{ 
                              height: 22, 
                              fontSize: '0.65rem',
                              bgcolor: result.type === 'sofa' ? '#eff6ff' : result.type === 'user' ? '#f1f5f9' : '#f0fdf4',
                              color: result.type === 'sofa' ? '#1e40af' : result.type === 'user' ? '#475569' : '#059669'
                            }}
                          />
                        </ListItemButton>
                      ))}
                    </Box>
                  )}

                  {/* Нет результатов */}
                  {searchQuery && searchQuery.length >= 2 && searchResults.length === 0 && !searchLoading && (
                    <Box sx={{ p: 3, textAlign: 'center' }}>
                      <SearchIcon sx={{ fontSize: 40, color: '#e2e8f0', mb: 1 }} />
                      <Typography variant="body2" sx={{ color: '#64748b' }}>
                        Ничего не найдено по запросу "{searchQuery}"
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                        Попробуйте изменить запрос
                      </Typography>
                    </Box>
                  )}

                  {/* Подсказка */}
                  <Box sx={{ 
                    px: 2, 
                    py: 1.25, 
                    bgcolor: '#f8fafc', 
                    borderTop: '1px solid #f1f5f9',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Chip label="Enter" size="small" sx={{ height: 20, fontSize: '0.65rem', bgcolor: '#e2e8f0' }} />
                      <Typography variant="caption" sx={{ color: '#94a3b8' }}>выбрать</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Chip label="Esc" size="small" sx={{ height: 20, fontSize: '0.65rem', bgcolor: '#e2e8f0' }} />
                      <Typography variant="caption" sx={{ color: '#94a3b8' }}>закрыть</Typography>
                    </Box>
                  </Box>
                </Paper>
              )}
            </Box>
          </ClickAwayListener>

          <Box sx={{ flexGrow: 1 }} />

          {/* Уведомления */}
          <Tooltip title="Уведомления">
            <IconButton 
              aria-label="Открыть уведомления"
              color="inherit" 
              onClick={handleNotificationsOpen}
              sx={{ 
                mr: 1,
                borderRadius: 2,
                '&:hover': { backgroundColor: 'rgba(30, 64, 175, 0.08)' }
              }}
            >
              <Badge badgeContent={unreadCount} color="error">
                <NotificationsIcon />
              </Badge>
            </IconButton>
          </Tooltip>
          
          {/* Меню уведомлений */}
          <Menu
            anchorEl={notificationsAnchor}
            open={Boolean(notificationsAnchor)}
            onClose={handleNotificationsClose}
            PaperProps={{
              sx: {
                width: { xs: 'calc(100vw - 24px)', sm: 380 },
                maxHeight: 480,
                borderRadius: 3,
                boxShadow: '0 10px 40px rgba(0, 0, 0, 0.15)',
                mt: 1,
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
              }
            }}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          >
            <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#0f172a' }}>
                  Уведомления
                </Typography>
                <Typography variant="caption" sx={{ color: '#64748b' }}>
                  {unreadCount > 0 ? `У вас ${unreadCount} непрочитанных` : 'Нет новых уведомлений'}
                </Typography>
              </Box>
              {notifications.length > 0 && (
                <Tooltip title="Очистить все">
                  <IconButton 
                    aria-label="Отметить все уведомления прочитанными"
                    size="small" 
                    onClick={handleMarkAllRead}
                    sx={{ 
                      color: '#64748b',
                      '&:hover': { color: '#1e40af', backgroundColor: 'rgba(30, 64, 175, 0.08)' }
                    }}
                  >
                    <DoneAllIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
            
            <Box sx={{ flex: 1, overflowY: 'auto', maxHeight: 360 }}>
            {notificationsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={24} />
              </Box>
            ) : notifications.length === 0 ? (
              <Box sx={{ py: 4, textAlign: 'center' }}>
                <NotificationsIcon sx={{ fontSize: 48, color: '#cbd5e1', mb: 1 }} />
                <Typography variant="body2" sx={{ color: '#94a3b8' }}>
                  Нет уведомлений
                </Typography>
              </Box>
            ) : (
              notifications.map((notification) => (
                <MenuItem 
                  key={notification.id} 
                  sx={{ 
                    py: 1.5, 
                    px: 2,
                    whiteSpace: 'normal',
                    '&:hover': { backgroundColor: '#f8fafc' },
                    borderBottom: '1px solid #f1f5f9',
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40, alignSelf: 'flex-start', mt: 0.5 }}>
                    {getNotificationIcon(notification.type)}
                  </ListItemIcon>
                  <Box sx={{ flex: 1, mr: 1, minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#0f172a' }}>
                      {notification.title}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#64748b', display: 'block', wordBreak: 'break-word' }}>
                      {notification.message}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#94a3b8', fontSize: '0.65rem' }}>
                      {notification.created_at ? new Date(notification.created_at).toLocaleString('ru-RU', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''}
                    </Typography>
                  </Box>
                  <Tooltip title="Прочитано">
                    <IconButton 
                      aria-label={`Отметить уведомление «${notification.title}» прочитанным`}
                      size="small" 
                      onClick={(e) => { e.stopPropagation(); handleMarkRead(notification.id); }}
                      sx={{ 
                        alignSelf: 'flex-start',
                        mt: 0.5,
                        color: '#94a3b8',
                        '&:hover': { color: '#059669', backgroundColor: 'rgba(5, 150, 105, 0.08)' }
                      }}
                    >
                      <DoneAllIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </MenuItem>
              ))
            )}
            </Box>
            
            <Divider />
            <MenuItem 
              onClick={() => { handleNotificationsClose(); navigate('/notifications'); }}
              sx={{ 
                justifyContent: 'center', 
                py: 1.5,
                color: '#3b82f6',
                fontWeight: 600,
                fontSize: '0.875rem',
              }}
            >
              Показать все уведомления
            </MenuItem>
          </Menu>

          {/* Профиль пользователя */}
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1.5,
            flexShrink: 0,
            ml: 1,
            pl: 2,
            borderLeft: '1px solid rgba(0, 0, 0, 0.08)'
          }}>
            <Box sx={{ textAlign: 'right', maxWidth: 180, overflowWrap: 'anywhere', display: { xs: 'none', md: 'block' } }}>
              <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary', lineHeight: 1.2 }}>
                {user?.full_name || 'Пользователь'}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
                {getRoleDisplayName(user?.role)}
              </Typography>
            </Box>
            <Avatar 
              sx={{ 
                width: 40, 
                height: 40, 
                bgcolor: getRoleColor(user?.role),
                fontSize: '0.875rem',
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'transform 0.2s',
                '&:hover': {
                  transform: 'scale(1.05)',
                }
              }}
            >
              {getInitials(user?.full_name)}
            </Avatar>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Боковое меню */}
      <Drawer
        variant={isMobile ? 'temporary' : 'permanent'}
        open={open}
        onClose={() => setOpen(false)}
        ModalProps={{ keepMounted: true }}
        sx={isMobile ? { '& .MuiDrawer-paper': { position: 'fixed', width: drawerWidth } } : undefined}
      >
        {/* Логотип */}
        <Toolbar
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: open ? 'flex-start' : 'center',
            px: open ? 3 : 1,
            minHeight: '64px !important',
            flexShrink: 0,
            borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
          }}
        >
          {open ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box
                component="img"
                src="/logo.jpg"
                alt="GallantCRM"
                sx={{ width: 36, height: 36, borderRadius: 2, objectFit: 'cover' }}
              />
              <Box>
                <Typography variant="subtitle1" sx={{ 
                  color: '#fff', 
                  fontWeight: 700, 
                  lineHeight: 1.1,
                  fontSize: '0.95rem'
                }}>
                  GallantCRM
                </Typography>
                <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.65rem' }}>
                  Управление производством
                </Typography>
              </Box>
            </Box>
          ) : (
            <Box
              component="img"
              src="/logo.jpg"
              alt="GallantCRM"
              sx={{ width: 36, height: 36, borderRadius: 2, objectFit: 'cover' }}
            />
          )}
        </Toolbar>

        {/* Навигация */}
        <Box sx={{ 
          flexGrow: 1, 
          minHeight: 0,
          overflowY: 'auto', 
          overflowX: 'hidden',
          py: 1,
          '&::-webkit-scrollbar': {
            width: 4,
          },
          '&::-webkit-scrollbar-track': {
            background: 'transparent',
          },
          '&::-webkit-scrollbar-thumb': {
            background: '#475569',
            borderRadius: 2,
          },
        }}>
          <List component="nav" disablePadding>
            {mainMenuItems.map(renderMenuItem)}
            {productionItems.length > 0 && renderSection('Производство', productionItems)}
            {resourceItems.length > 0 && renderSection('Ресурсы', resourceItems)}
            {financeItems.length > 0 && renderSection('Финансы', financeItems)}
          </List>
        </Box>

        {/* Кнопка выхода */}
        <Box sx={{ p: 2, flexShrink: 0, borderTop: '1px solid rgba(255, 255, 255, 0.08)' }}>
          <ListItemButton 
            onClick={onLogout}
            sx={{
              borderRadius: 2,
              color: '#ef4444',
              py: 1.25,
              justifyContent: open ? 'initial' : 'center',
              '&:hover': {
                backgroundColor: 'rgba(239, 68, 68, 0.1)',
              },
            }}
          >
            <ListItemIcon sx={{ 
              minWidth: open ? 40 : 0, 
              color: 'inherit',
              justifyContent: 'center',
            }}>
              <LogoutIcon />
            </ListItemIcon>
            {open && (
              <ListItemText 
                primary="Выйти из системы" 
                primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: 500 }}
              />
            )}
          </ListItemButton>
        </Box>
      </Drawer>

      {/* Основной контент */}
      <Box
        component="main"
        sx={{
          background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
          flex: '1 1 0%',
          minHeight: '100dvh',
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Toolbar sx={{ minHeight: '64px !important', flexShrink: 0 }} />
        <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 }, flex: '1 0 auto', minWidth: 0 }}>
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
}
